import { mergeClassNames } from "../../utils/classes";
import { Label } from "./Label";

import type { RuleValidationResult } from "../../types/validation";
import type { ReactNode } from "react";

import * as styles from "./labelFor.module.scss";

export interface LabelTooltip {
	message: ReactNode;
}

export interface LabelBadge {
	name: string;
	onClick: () => void;
}

interface LabelForProps {
	label: ReactNode;
	hint?: ReactNode;
	children?: ReactNode;
	tooltip?: ReactNode;
	errors?: readonly unknown[] | undefined;
	className?: string | undefined;
	testId?: string | undefined;
	htmlFor?: string | undefined;
	innerLabelClassName?: string | undefined;
	markAsRequired?: boolean | undefined;
	/** Render extra content next to the label text (e.g., info tooltip). */
	renderLabelExtra?: (() => ReactNode) | undefined;
	/** Render footer replacing default validation. Takes full control of the bottom section. */
	renderFooter?: ((errors: readonly unknown[] | undefined, testId: string | undefined) => ReactNode) | undefined;
	/** Override just validation rendering. */
	renderValidation?: ((errors: readonly unknown[] | undefined, testId: string | undefined) => ReactNode) | undefined;
}

const LabelFor = ({
	label,
	hint,
	children,
	errors,
	className,
	tooltip,
	testId,
	htmlFor,
	innerLabelClassName,
	markAsRequired = false,
	renderLabelExtra,
	renderFooter,
	renderValidation,
}: LabelForProps) => (
	<label htmlFor={htmlFor} data-testid={testId} className={mergeClassNames(styles.label, className)}>
		{label && (
			<div className={styles.flex}>
				<Label label={label} hint={hint} className={innerLabelClassName} markAsRequired={markAsRequired} />
				{renderLabelExtra?.()}
			</div>
		)}
		{tooltip ? (
			<div className={styles.flex}>
				{children}
				{tooltip}
			</div>
		) : (
			children
		)}
		{renderFooter
			? renderFooter(errors, testId)
			: renderValidation
				? renderValidation(errors, testId)
				: errors &&
					errors.length > 0 && (
						<div className={styles.validationErrors}>
							{errors.map((error, index) => (
								<span key={index} className={styles.validationError}>
									{(error as RuleValidationResult).message}
								</span>
							))}
						</div>
					)}
	</label>
);

export default LabelFor;
